import { devDebug } from "./devLog";
import axios from "axios";
import {
  deriveKeypair,
  loadIdentity,
  signMessage,
  unlockMnemonic,
} from "./identity";
import { sha256Hex } from "./hash";
import { MUTATION_TIMEOUT_MS, REQUEST_TIMEOUT_MS } from "./apiConstants";

const API = process.env.REACT_APP_BACKEND_URL || "";

// The signing key is derived once per session and reused. We keep it in memory
// only so the mnemonic is never handed to code that doesn't need it again.
let cachedPrivateKeyHex = null;
async function getPrivateKey() {
  if (cachedPrivateKeyHex) return cachedPrivateKeyHex;
  const mnemonic = await unlockMnemonic();
  if (!mnemonic) throw new Error("Impossibile sbloccare la chiave privata");
  const keys = await deriveKeypair(mnemonic.split(" "));
  cachedPrivateKeyHex = keys.privateKeyHex;
  return cachedPrivateKeyHex;
}

export function clearSigningCache() {
  cachedPrivateKeyHex = null;
}

async function requestNonce(identityId) {
  const r = await axios.post(
    `${API}/api/v1/auth/challenge`,
    { identityId },
    { timeout: REQUEST_TIMEOUT_MS, withCredentials: true },
  );
  return r.data.nonce;
}

/**
 * Sends a protected request. Ordinary public actions use only the automatic
 * HttpOnly session. DM endpoints and profile-key bootstrap remain signed.
 */
export async function signedGet(path) {
  return authenticatedRequest("GET", path, null);
}

export async function signedPost(path, body) {
  return authenticatedRequest("POST", path, body);
}

export async function signedDelete(path) {
  return authenticatedRequest("DELETE", path, null);
}

export async function signedPatch(path, body) {
  return authenticatedRequest("PATCH", path, body);
}

/**
 * Opens or renews the ordinary public-session cookie with one explicit signed
 * request. This is called automatically at registration/login and app resume,
 * never when the user presses a public social-action button.
 */
export async function bootstrapPublicSession() {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  return signedRequestWithNonce("POST", "/api/v1/auth/session", {}, identity);
}

function isRetryableSignatureFailure(error) {
  if (error?.response?.status !== 401) return false;
  const payload = error?.response?.data;
  const message = typeof payload === "string"
    ? payload
    : payload?.message || payload?.detail || "";
  return /nonce|firma|signature/i.test(String(message));
}

/**
 * Profile bootstrap/update changes the public and DM keys, so it remains
 * strictly signed. Every direct-message operation also remains strictly
 * signed and never accepts the ordinary social-session cookie.
 */
export function requiresStrictSignature(method, path) {
  const cleanPath = String(path || "").split("?", 1)[0];
  if (cleanPath === "/api/v1/dm" || cleanPath.startsWith("/api/v1/dm/")) {
    return true;
  }
  return method.toUpperCase() !== "GET" && cleanPath === "/api/v1/profiles";
}

function requestBody(body) {
  return body == null ? "" : JSON.stringify(body);
}

async function requestWithPublicSession(method, path, body, identity) {
  const bodyStr = requestBody(body);
  const headers = { "x-ol-identity": identity.identityId };
  if (body != null) headers["content-type"] = "application/json";
  return axios.request({
    url: `${API}${path}`,
    method,
    data: body != null ? bodyStr : undefined,
    headers,
    transformRequest: [(d) => d],
    timeout: MUTATION_TIMEOUT_MS,
    withCredentials: true,
  });
}

function publicSessionExpired(error) {
  return error?.response?.status === 401;
}

/**
 * Ordinary public actions never reopen the private key. The signed path is
 * reserved for registration/profile-key bootstrap and direct messages.
 * A missing or expired public session is surfaced explicitly so the UI can
 * reset the local test profile or ask the user to sign in again.
 */
async function authenticatedRequest(method, path, body) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");

  if (requiresStrictSignature(method, path)) {
    return signedRequestWithNonce(method, path, body, identity);
  }

  try {
    return await requestWithPublicSession(method, path, body, identity);
  } catch (error) {
    if (!publicSessionExpired(error)) throw error;
    devDebug("publicSession: sessione assente o scaduta", path);
    const sessionError = new Error(
      "Sessione scaduta o non valida. Esci dal profilo e accedi di nuovo.",
    );
    sessionError.code = "PUBLIC_SESSION_EXPIRED";
    sessionError.cause = error;
    throw sessionError;
  }
}

/**
 * Legacy-compatible signed request. Private-message and profile-key operations
 * always use it directly. Signed requests are serialized per tab to protect
 * one-time nonces from concurrent mutations.
 */
async function signedRequestWithNonce(method, path, body, identity) {
  const prev = pendingChain;
  const next = (async () => {
    try {
      await prev;
    } catch (err) {
      devDebug("signedRequest prev-chain error (already surfaced)", err?.message);
    }

    const bodyStr = requestBody(body);
    const bodyHash = sha256Hex(bodyStr);
    // Server strips the query string before verifying the signature (see
    // SignedRequestGuard), so sign only the path portion.
    const signPath = path.split("?", 1)[0];
    const privateKeyHex = await getPrivateKey();

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const nonce = await requestNonce(identity.identityId);
      const message = `${method} ${signPath}\n${nonce}\n${bodyHash}`;
      const signatureHex = await signMessage(privateKeyHex, message);
      const headers = {
        "x-ol-identity": identity.identityId,
        "x-ol-nonce": nonce,
        "x-ol-signature": signatureHex,
      };
      if (body != null) headers["content-type"] = "application/json";

      try {
        return await axios.request({
          url: `${API}${path}`,
          method,
          data: body != null ? bodyStr : undefined,
          headers,
          transformRequest: [(d) => d],
          timeout: MUTATION_TIMEOUT_MS,
          withCredentials: true,
        });
      } catch (error) {
        if (attempt === 0 && isRetryableSignatureFailure(error)) {
          devDebug("signedRequest: nonce/firma rifiutati, nuovo tentativo", path);
          continue;
        }
        throw error;
      }
    }
    throw new Error("Richiesta firmata non completata");
  })();
  pendingChain = next;
  return next;
}

let pendingChain = Promise.resolve();
